import { ref } from 'vue'

import { asyncComputed, until } from '@vueuse/core'

import useLayers from '@tsyl/karonte/lib/composables/useLayers'
import useView from '@tsyl/karonte/lib/composables/useView'
import type { Coordinates } from '@tsyl/karonte/lib/types/coordinates'
import type MdiGraphicsLayer from '@tsyl/karonte-tsyl/lib/layers/MdiGraphicsLayer'
import { webMercatorToLatLon } from '@tsyl/radon'

import type { Bookmark } from '@/types/Bookmark'

export default function bookmarksComposable() {
  const { allLayers: karonteLayers, asyncGetLayerById } = useLayers()

  const { getReactiveView } = useView()

  const view = getReactiveView()

  const bookMarks = ref([] as Array<Bookmark>)

  const bookmarkLayer = asyncComputed(async () => {
    return await asyncGetLayerById<MdiGraphicsLayer>('bookmarks')
  }, undefined)

  async function showBookmarks(bookmarks: Bookmark[]) {
    await until(bookmarkLayer).toBeTruthy()
    bookmarkLayer.value?.clear()
    bookMarks.value = bookmarks
    bookMarks.value.forEach((b) => {
      printBookmark(b)
    })
  }

  function addBookmark(bookmark: Bookmark) {
    printBookmark(bookmark)
    bookMarks.value.push(bookmark)
  }

  function deleteBookmark(bookmark: Bookmark) {
    bookmarkLayer.value?.removeGeometries(bookmark.id as string)
  }

  function goToBookmark(bookmark: Bookmark) {
    if (bookmark) {
      const coords = webMercatorToLatLon(Number(bookmark.x), Number(bookmark.y))
      const coordinates: Coordinates = {
        latitude: coords.lat,
        longitude: coords.lon
      }
      view.value?.goTo(coordinates, bookmark.zoom ?? 15)
    }
  }

  function printBookmark(bookmark: Bookmark) {
    const coords = webMercatorToLatLon(Number(bookmark.x), Number(bookmark.y))
    bookmarkLayer.value?.addGeometry({
      id: bookmark.id,
      type: 'point',
      attributes: { name: bookmark.icon },
      matchingKey: 'name',
      coordinates: { longitude: coords.lon, latitude: coords.lat },
      symbolProperties: { color: bookmark.color, size: 25, rotation: 0 }
    })
  }

  return {
    addBookmark,
    deleteBookmark,
    goToBookmark,
    showBookmarks,
    bookmarkLayer,
    karonteLayers
  }
}
